import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import useSencoPupils from '../../hooks/useSencoPupils';
import { getOrgConfig } from '../../utils/orgResolution';
import { buildFamilyChild, familyUserName } from '../../utils/familyPortal';
import FamilyPortalShell from './FamilyPortalShell';
import FamilyLoadError from './FamilyLoadError';
import FamilyLoadingState from './FamilyLoadingState';
import FamilyChildCard from './FamilyChildCard';
import './familyYourChildren.css';

/*
 * Family portal · Your children (PC-04). One card per child this guardian is
 * linked to; the whole card is one link to the child's page. Mobile: a single
 * column. Desktop: the designed row of 536 cards under the page header. A
 * guardian with exactly one child goes straight to that child's page on
 * landing (flow 2: "Only one child?"), unless they chose Your children from
 * the menu.
 */
export default function FamilyYourChildrenPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, claims, forceTokenRefresh } = useAuth();
  const claimsOrgId = claims?.rmsOrgId;
  const orgId = claimsOrgId || getOrgConfig().orgId;
  // The invite link lands here (TDI-676): a first visit links the account and
  // then needs the refreshed token before it can read the child's record.
  const { pupils, loading, error, reload, invitationRetired } = useSencoPupils({ orgId, claimsOrgId, forceTokenRefresh });

  // School users (and anyone else) never belong here.
  useEffect(() => {
    if (claims && claims.userType && claims.userType !== 'RMS Legal Guardian') {
      navigate('/rms/dashboard', { replace: true });
    }
  }, [claims, navigate]);

  const children = useMemo(() => pupils.map(buildFamilyChild).filter(Boolean), [pupils]);

  // One child: land on their page directly. Menu navigation passes
  // state.stay so the list is still reachable.
  useEffect(() => {
    if (!loading && !error && children.length === 1 && !location.state?.stay) {
      navigate(`/rms/family/child/${children[0].id}`, { replace: true });
    }
  }, [loading, error, children, location.state, navigate]);

  const userName = familyUserName(user, pupils);

  return (
    <FamilyPortalShell userName={userName} maxWidth="full">
      <div className="family-page-head">
        <h1 className="family-page-head__title family-page-head__title--home">Your children</h1>
        <p className="family-page-head__intro">Choose your child’s name to see their details and questions.</p>
      </div>

      {loading && <FamilyLoadingState />}

      {!loading && error && (
        <FamilyLoadError onRetry={() => reload()} />
      )}

      {!loading && !error && children.length === 0 && (
        invitationRetired ? (
          // Their invitation was retired (for example the school changed the
          // parent's email), but the old link still signs them in: say why
          // nothing is here rather than hinting at a school mistake (TDI-803).
          <p className="family-empty">
            This invitation is no longer valid. If you were expecting to see
            your child here, contact their school.
          </p>
        ) : (
          <p className="family-empty">
            There are no children linked to this account yet. If you were expecting
            to see your child here, contact their school.
          </p>
        )
      )}

      {!loading && !error && children.length > 0 && (
        <ul className="family-child-cards">
          {children.map((child) => (
            <li key={child.id}>
              <FamilyChildCard child={child} />
            </li>
          ))}
        </ul>
      )}
    </FamilyPortalShell>
  );
}
